import { TitleCasePipe } from '@angular/common';
import { Component, EventEmitter, Input, OnChanges, Output, SimpleChanges, ViewEncapsulation } from '@angular/core';
import {
  ASSET_BREAKDOWN_TAB, FINANCIAL_REPORT_TABS, FinancialFigure, FinancialReportPeriods, FinancialReportTable,
  assetBreakdownRows, assetBreakdownSection, financialPeriodDetails, financialPeriodTableColumns, financialReportInitialTab,
  financialReportTable, financialPeriodsText, financialSections,
} from './financial-report';
import { PeriodTableColumn, PeriodTableRow } from './company-profile-period-table.component';

/**
 * Shared financial report: tabs for the profit and loss account, balance sheet, asset breakdown and other metrics.
 * periods="latest-two" (Logistics) compares the latest period with the prior one in each row. periods="latest-five" (Tax)
 * lays out the five most recent filed periods (fewer when fewer are filed) in the shared period table: columns newest first, with a
 * sticky metric column and header and a subtle year-on-year change under each figure. Place it inside the profile's own dialog.
 * Skins theme it with --profile-report-*.
 */
@Component({
  selector: 'company-profile-financial-report',
  templateUrl: './company-profile-financial-report.component.html',
  styleUrls: ['./company-profile-financial-report.component.css'],
  encapsulation: ViewEncapsulation.None,
  standalone: false,
  host: { 'class': 'profile-financial-report', '[class.profile-financial-report--latest-five]': "periods === 'latest-five'" },
})
export class CompanyProfileFinancialReportComponent implements OnChanges {
  /** financials.accounts from company_profile_data. */
  @Input() accounts: Record<string, any> | null | undefined = null;
  /** asset_breakdown from company_profile_data. */
  @Input() assetBreakdown: Record<string, any> | null | undefined = null;
  @Input() periods: FinancialReportPeriods = 'latest-two';
  /** The open tab. Leave unset to open the first tab with figures. */
  @Input() tab: string | null = null;
  @Output() tabChange = new EventEmitter<string>();

  readonly tabs = FINANCIAL_REPORT_TABS;
  readonly assetTab = ASSET_BREAKDOWN_TAB;
  readonly periodsText = financialPeriodsText;
  activeTab = FINANCIAL_REPORT_TABS[0];
  showChange = true;
  sections: ReturnType<typeof financialSections> = [];
  assetRows: FinancialFigure[] = [];
  assetDates: string[] = [];
  tables: Record<string, FinancialReportTable> = {};
  periodColumns: Record<string, PeriodTableColumn[]> = {};
  periodRows: Record<string, PeriodTableRow[]> = {};
  private readonly titleCase = new TitleCasePipe();

  ngOnChanges(changes: SimpleChanges): void {
    if (!changes['accounts'] && !changes['assetBreakdown'] && !changes['periods']) {
      if (changes['tab'] && this.tab) this.activeTab = this.tab;
      return;
    }
    this.sections = financialSections(this.accounts);
    this.assetRows = assetBreakdownRows(this.assetBreakdown);
    const assets = assetBreakdownSection(this.assetBreakdown);
    this.assetDates = (assets.columns || []).map(column => column.column_name);
    const periodDetails = financialPeriodDetails(this.accounts);
    this.tables = {};
    this.periodColumns = {};
    this.periodRows = {};
    for (const name of this.tabs) {
      const section = name === ASSET_BREAKDOWN_TAB ? assets : this.accounts?.[name];
      const table = financialReportTable(name, section, this.periods, periodDetails);
      this.tables[name] = table;
      this.periodColumns[name] = financialPeriodTableColumns(table);
      this.periodRows[name] = name === ASSET_BREAKDOWN_TAB
        ? table.rows.map(row => ({ ...row, name: this.titleCase.transform(row.name) })) : table.rows;
    }
    // New figures open the requested tab when it has any, otherwise the first tab that does.
    this.activeTab = financialReportInitialTab(this.accounts, this.assetBreakdown, this.tab || undefined);
  }

  get activeSection() {
    return this.sections.find(section => section.name === this.activeTab);
  }

  get activeTable(): FinancialReportTable {
    return this.tables[this.activeTab];
  }

  selectTab(tab: string): void {
    this.activeTab = tab;
    this.tabChange.emit(tab);
  }

  tabLabel(tab: string): string {
    return tab === 'Profit and Loss Account' ? 'P&L' : tab === 'Other Financial Metrics' ? 'Other metrics' : tab;
  }

  /** The Logistics comparison formatter, kept as it was so that report looks the same. */
  latestTwoFigure(value: unknown, type = 'funds'): string {
    if (value === null || value === undefined || value === '') return '—';
    if (typeof value !== 'number' && !Number.isFinite(Number(value))) return String(value);
    const number = Number(value);
    return `${type === 'funds' ? '£' : ''}${new Intl.NumberFormat('en-GB', { maximumFractionDigits: 0 }).format(number)}`;
  }
}
